import { Grid, Container, CircularProgress, Box } from '@mui/material';
import React, { useState, useEffect, useRef } from 'react';
import GeocodingMap from '../../components/GeocodingMap';
import Button from '@mui/material/Button';
import OutlinedInput from '@mui/material/OutlinedInput';
import { deliverableAddressRepo, shipmentRepo } from '../../repositories';
import { H5, H4 } from '../../components/Heading';
import { ColorBox } from '../../components/Box';
import { Apple, Google } from '../../components/Icons';
import { Search } from '@mui/icons-material';
import Copiable from '../../components/Copiable';
import { useRecoilValue } from 'recoil';
import { incidentDetailState } from '../../states/incidents';
import { selectedShipmentIdState } from '../../states/shipment';
import AxlModalConfirm from '../../components/AxlModalConfirm';

export default function AddressEditorContainer({ orig, onClose, onSave, onOverride, classes }) {
  const [address, setAddress] = useState(orig);
  const [unserviceable, setUnserviceable] = useState(false);
  const [confirmUpdateAddress, setConfirmUpdateAddress] = useState(false);
  const [latLng, setLatLng] = useState();
  const [goodToGo, setGoodToGo] = useState();
  const [geocoded, setGeocoded] = useState();
  const [zoom, setZoom] = useState();
  const [validation, setValidation] = useState(undefined);
  const [flagged, setFlagged] = useState([]);
  // Holds DAS's suggested field values when it silently "corrects" something, so the
  // user can opt in to them via "Use suggested address" instead of having their typed
  // text replaced automatically. Null whenever there is no pending suggestion.
  const [suggestedAddress, setSuggestedAddress] = useState(null);
  const [fullAddress, setFullAddress] = useState();
  const [loading, setLoading] = useState(false);
  const preValidationAddressRef = useRef(orig);
  // Identifies the most recently started validate() call. Any in-flight request whose
  // id no longer matches this ref (because a newer validate() call started, or orig
  // changed) is stale and must not be allowed to apply its state updates.
  const validationRequestIdRef = useRef(0);
  const selectedIncident = useRecoilValue(incidentDetailState);
  const shipmentId = useRecoilValue(selectedShipmentIdState);

  useEffect(() => {
    setAddress(orig);
    setLatLng(orig?.lat ? { lat: orig.lat, lng: orig.lng } : null);
    setValidation(null);
    setFlagged([]);
    setUnserviceable(false);
    setSuggestedAddress(null);
    preValidationAddressRef.current = orig;
    // orig changed, so any validation currently in flight is for a different address now.
    // Bump the request id so its (now-stale) response is dropped on arrival, and reset
    // loading ourselves since that stale response's early-return will no longer do it.
    validationRequestIdRef.current += 1;
    setLoading(false);
  }, [orig]);

  const isDiff = (str, orig) => {
    if (str === null || str === undefined) {
      //we will let empty strings through
      return false;
    }
    return (str && !orig) || (orig && !str) || (str && orig && str.trim().toUpperCase() !== orig.trim().toUpperCase());
  };

  const isDiffZip = (addressComponents, orig) => {
    if (!addressComponents?.componentNotes?.zip && !addressComponents?.componentNotes?.zip4) {
      return false;
    }

    if (!!addressComponents?.componentNotes?.zip) {
      return true;
    }

    if (orig.includes('-') && !!addressComponents?.zip4) {
      return true;
    }
    return false;
  };

  const handleSave = () => {
    if (unserviceable) {
      setConfirmUpdateAddress(true);
    } else {
      onSave?.(address);
    }
  };

  const getDiffZip = (addressComponents, orig) => {
    if (!addressComponents?.componentNotes?.zip && !addressComponents?.componentNotes?.zip4) {
      return null;
    }

    let zip4 = '';
    if (orig.includes('-') && !!addressComponents?.zip4) {
      zip4 = `-${addressComponents?.zip4}`;
    }
    return `${addressComponents.zip}${zip4}`;
  };

  const validate = async () => {
    const requestId = (validationRequestIdRef.current += 1);
    const isStale = () => requestId !== validationRequestIdRef.current;
    setLoading(true);
    setUnserviceable(false);
    // snapshot the user-entered address before DAS correction may overwrite it,
    // so the override button can still submit what the user originally typed
    preValidationAddressRef.current = address;
    try {
      let params = { ...address, street1: address?.street, force_true: 'true' };

      const response = await deliverableAddressRepo.get(params);
      // a newer validate() call (or an orig change) superseded this one while it was
      // in flight -- drop this response instead of clobbering newer state with it
      if (isStale()) {
        return;
      }
      if (response.status === 200) {
        const geocode = response.data.geocode;

        let isCorrected = false;
        // updatedAddress keeps the user's typed field values -- only lat/lng from this
        // geocode call are applied to it. DAS's corrected field values are collected
        // separately into `suggested` below so they never silently replace what the
        // user typed; the user opts into them via "Use suggested address".
        let updatedAddress = { ...address, lat: geocode.latitude, lng: geocode.longitude };
        let corrections = [];
        let suggested = null;
        if (response?.data?.addressComponents?.isCorrected === true) {
          const addressComponents = response.data?.addressComponents;
          const candidate = { ...updatedAddress };

          if (isDiff(addressComponents?.streetLine, candidate.street)) {
            candidate.street = addressComponents?.streetLine;
            corrections.push('corrected street to ' + addressComponents?.streetLine);
          }

          if (isDiff(addressComponents?.secondaryAddressLine, candidate.street2)) {
            candidate.street2 = addressComponents?.secondaryAddressLine;
            corrections.push('corrected street2 to ' + addressComponents?.secondaryAddressLine);
          }
          if (!!addressComponents?.componentNotes?.city) {
            candidate.city = addressComponents?.city;
            corrections.push('corrected city to ' + addressComponents?.city);
          }

          if (!!addressComponents?.componentNotes?.state) {
            candidate.state = addressComponents?.state;
            corrections.push('corrected state to ' + addressComponents?.state);
          }
          if (isDiffZip(addressComponents, candidate.zipcode)) {
            const correctedZip = getDiffZip(addressComponents, candidate.zipcode);
            candidate.zipcode = correctedZip;
            corrections.push('corrected zipcode to ' + correctedZip);
          }

          isCorrected = true;
          if (corrections.length > 0) {
            suggested = candidate;
          }
        }

        const dasResponse = {
          status: 'success',
          isCorrected: isCorrected,
          corrections: corrections,
        };

        if (shipmentId) {
          const serviceable = await shipmentRepo.serviceabilityCheck(shipmentId, updatedAddress);
          // re-check after the second async call -- orig (or a newer validate() call)
          // may have superseded this request while serviceabilityCheck was pending
          if (isStale()) {
            return;
          }
          if (!serviceable) {
            dasResponse.status = 'unserviceable';
            dasResponse.message = 'The address is in unserviceable area';
            setUnserviceable(true);
          }
        }
        // commit all state for this request together, now that it's confirmed current
        setLatLng({ lat: geocode.latitude, lng: geocode.longitude });
        setGeocoded({ location: geocode });
        setValidation(dasResponse);
        setAddress(updatedAddress);
        setSuggestedAddress(suggested);
        setLoading(false);
        if (response.data?.flaggedIssues && response.data?.flaggedIssues.length > 0) {
          setFlagged(response.data.flaggedIssues);
        } else {
          setFlagged([]);
        }
      } else {
        setLatLng(null);
        const dasResponse = {
          status: 'failed',
          message: response.data?.message,
        };
        setValidation(dasResponse);
        setFlagged([]);
        setLoading(false);
      }
    } catch (err) {
      if (isStale()) {
        return;
      }
      setLoading(false);
      setValidation({ status: 'failed', message: err?.message || 'Validation error' });
      setFlagged([]);
      setUnserviceable(false);
    }
  };

  useEffect(() => {
    setGoodToGo(geocoded?.location && geocoded.location.latitude);
  }, [geocoded]);

  const updateAddress = (datatype) => (event) => {
    let data = {};
    data[datatype] = event.target.value;
    setAddress((a) => ({ ...a, ...data }));
    setGeocoded(null);
    setValidation(null);
    setFlagged([]);
    setSuggestedAddress(null);
  };

  const applySuggestedAddress = () => {
    if (suggestedAddress) {
      // The user has accepted this address, so if flagged issues keep the override
      // button visible, clicking it from here on should submit the accepted
      // suggestion -- not the stale pre-validation address.
      preValidationAddressRef.current = suggestedAddress;
      setAddress(suggestedAddress);
      setSuggestedAddress(null);
      // The user has explicitly accepted DAS's suggestion, so the corrections list and
      // the "Use suggested address"/override actions it drove are now stale -- there is
      // nothing left to suggest or override. Clear them while keeping the rest of the
      // validation result (status/source/geocode) intact.
      setValidation((v) => (v ? { ...v, isCorrected: false, corrections: [] } : v));
    }
  };

  useEffect(() => {
    const full = address ? `${address.street}, ${address.city}, ${address.state} ${address.zipcode}`.replaceAll('#', ' ') : '';
    setFullAddress(full);
  }, [address]);

  const addressEditor = () => {
    return (
      <>
        <AxlModalConfirm
          isOpen={confirmUpdateAddress}
          handleClose={() => {
            setConfirmUpdateAddress(false);
            setUnserviceable(false);
          }}
          componentChildren={<Box textAlign={'center'}>The updated address is in unserviceable area. Do you want to process?</Box>}
          textOK={'Continue'}
          handleOK={() => {
            onSave?.(address);
            setConfirmUpdateAddress(false);
            setUnserviceable(false);
          }}
          maxWidth={'xs'}
        />
        <Grid container>
          <Grid size={12}>
            <H4>Street</H4>
            <OutlinedInput onChange={updateAddress('street')} fullWidth style={{ height: '40px' }} value={address ? address.street : ''} />
          </Grid>
          <Grid size={12}>
            <H4>Street2</H4>
            <OutlinedInput onChange={updateAddress('street2')} fullWidth style={{ height: '40px' }} value={address ? address.street2 : ''} />
          </Grid>
          <Grid size={12}>
            <H4>City</H4>
            <OutlinedInput onChange={updateAddress('city')} fullWidth style={{ height: '40px' }} value={address ? address.city : ''} />
          </Grid>
          <Grid size={12} container spacing={1}>
            <Grid size={5}>
              <H4>State</H4>{' '}
            </Grid>
            <Grid size={7}>
              <H4>Zipcode</H4>{' '}
            </Grid>
            <Grid size={5}>
              <OutlinedInput onChange={updateAddress('state')} fullWidth style={{ height: '40px' }} value={address ? address.state : ''} />
            </Grid>
            <Grid size={7}>
              <OutlinedInput onChange={updateAddress('zipcode')} fullWidth style={{ height: '40px' }} value={address ? address.zipcode : ''} />
            </Grid>
          </Grid>
          <Grid size={12}>
            <ColorBox style={{ marginTop: '8px', fontSize: '13px', color: '#555' }}>
              <Copiable txt={fullAddress} />
              <span style={{ display: 'inline-block' }}>
                <a style={{ marginLeft: '10px' }} target="_blank" rel="noreferrer" href={`https://maps.google.com/?q=${fullAddress}`}>
                  <Google size="sm" />
                </a>
                <a style={{ marginLeft: '10px' }} target="_blank" rel="noreferrer" href={`https://maps.apple.com/?address=${fullAddress}`}>
                  <Apple size="md" />
                </a>
                <a style={{ marginLeft: '10px', color: '#444' }} target="_blank" rel="noreferrer" href={`https://google.com/search?q=${fullAddress}`}>
                  <Search style={{ marginBottom: '-4px' }} fontSize="small" />
                </a>
              </span>
            </ColorBox>
          </Grid>
          <Grid size={12} container>
            <Container style={{ display: 'flex', justifyContent: 'space-between', height: '40px', marginTop: '10px', padding: 0 }}>
              <Button style={{ fontSize: '11px' }} size={'small'} onClick={validate}>
                Validate and Geocode
              </Button>
              <Button style={{ fontSize: '11px' }} disabled={!goodToGo} size={'small'} onClick={() => handleSave()}>
                {!!selectedIncident ? 'Save and Resolve' : 'Save'}
              </Button>
              <Button style={{ fontSize: '11px' }} size={'small'} onClick={() => onClose && onClose()}>
                Close
              </Button>
            </Container>
          </Grid>
          <Grid size={12}>
            {validation && (
              <ColorBox color={validation.status || ''}>
                <H4>Geocode Result</H4>
                <Grid container>
                  <Grid size={3}>
                    <H5>status:</H5>
                  </Grid>
                  <Grid size={9}>
                    {validation.status}
                  </Grid>
                  {validation.status === 'success' ? (
                    <Grid container>
                      <Grid size={3}>
                        <H5>source:</H5>
                      </Grid>
                      <Grid size={9}>
                        {geocoded?.location.source}
                      </Grid>
                      {geocoded.location && (
                        <Grid size={12}>
                          result: {geocoded.location.latitude}, {geocoded.location.longitude}
                        </Grid>
                      )}
                    </Grid>
                  ) : (
                    <Grid container>
                      <Grid size={3}>
                        <H5>error:</H5>
                      </Grid>
                      <Grid size={12}>
                        {validation.message}
                      </Grid>
                    </Grid>
                  )}
                  {validation.isCorrected && (
                    <Grid container style={{ marginTop: '8px', fontSize: '13px', color: 'red' }}>
                      <Grid size={3}>
                        <H5>Corrections:</H5>
                      </Grid>
                      {validation?.corrections.map((correction, i) => {
                        return (
                          <Grid size={12} key={`item-correction-${i}`}>
                            {correction}
                          </Grid>
                        );
                      })}
                      {suggestedAddress && (
                        <Grid size={12} style={{ marginTop: '4px' }}>
                          <Button style={{ fontSize: '11px' }} size={'small'} onClick={applySuggestedAddress}>
                            Use suggested address
                          </Button>
                        </Grid>
                      )}
                    </Grid>
                  )}
                </Grid>
              </ColorBox>
            )}
          </Grid>
          <Grid size={12}>
            {flagged && flagged.length > 0 && (
              <ColorBox color={'failed'}>
                <H4>Address was retrieved from cache but it appears to have some validation issues:</H4>
                <Grid container>
                  {flagged.map((flag, i) => {
                    return (
                      <Grid size={12} key={`item-flagged-${i}`}>
                        {flag}
                      </Grid>
                    );
                  })}
                </Grid>
              </ColorBox>
            )}
          </Grid>
          <Grid size={12}>
            {validation?.status && (validation.status !== 'success' || flagged.length > 0 || validation.corrections?.length > 0) && (
              <ColorBox color={validation.status || ''}>
                <Grid container>
                  <Grid size={8}>
                    <Button style={{ background: 'red' }} onClick={() => onOverride && onOverride(preValidationAddressRef.current)}>
                      Click to OVERRIDE Address
                    </Button>
                  </Grid>
                </Grid>
              </ColorBox>
            )}
          </Grid>
        </Grid>
      </>
    );
  };

  return (
    <Grid container spacing={1}>
      <Grid size={5} style={{ minHeight: '540px' }}>
        {loading ? <CircularProgress color="primary" size={20} /> : addressEditor()}
      </Grid>
      <Grid size={7}>
        <GeocodingMap center={latLng} initialZoom={zoom} google_key={`${import.meta.env.REACT_APP_GOOGLE_KEY}`} markers={latLng ? [{ latLng }] : []} />
      </Grid>
    </Grid>
  );
}
